import React, { useMemo, useState } from 'react';
import useInput from 'lib/hooks/useInput';
import { Grid, TextField } from '@material-ui/core';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { invalidFormType, pageUrl } from 'lib/mapper';
import { useShallowAppSelector } from 'store/hooks';
import { IApiReponse } from 'lib/types/sharedTypes';
import CustomText from 'components/common/text/CustomText';
import { T } from 'components/common/text';
import { EmployeeActions, AppActions } from 'store/actionCreators';
import AuthLayout from 'components/auth/AuthLayout';
import { Trans } from 'react-i18next';
import CustomSpan from 'components/common/text/CustomSpan';
import { color } from 'styles/utils';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDidUpdateEffect } from 'lib/utils';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';

export default function PasswordInquiryContainer({ jestSubmit }: { jestSubmit: (e: any) => void }) {
  const {
    sendEmailForResetSuccess,
    sendEmailForResetError,
  }: {
    sendEmailForResetSuccess: boolean;
    sendEmailForResetError: IApiReponse;
  } = useShallowAppSelector(state => ({
    sendEmailForResetSuccess: state.employee.sendEmailForReset.success,
    sendEmailForResetError: state.employee.sendEmailForReset.error,
  }));
  const history = useHistory();
  const nicknameState = history.location.state?.nickname || '';
  const nickname = useInput(nicknameState);
  const email = useInput('');
  const isInvalidGroupCode = sendEmailForResetError?.message === invalidFormType.invalidGroupCode;
  const isInvalidEmail = sendEmailForResetError?.message === invalidFormType.invalidEmail;
  const isInvalidEmployee = sendEmailForResetError?.message === invalidFormType.invalidEmployee;
  const { t } = useTranslation();

  const isValidForm = useMemo(() => {
    return nickname.value?.trim() && email.value?.trim();
  }, [nickname.value, email.value]);

  // SECTION: function

  const handleSubmit = () => {
    // test code
    jestSubmit({
      nickname: nickname.value,
      email: email.value,
    });

    EmployeeActions.send_email_for_reset_request({ nickname: nickname.value, email: email.value });
  };

  const handleNavigateLogin = () => {
    history.push({ pathname: pageUrl.auth.signIn, state: { nickname: nickname.value } });
  };

  const popupComment: { title: string; mainText: string; subText: string } = {
    title: t('GLOBAL_SUCCESS'),
    mainText: t('SUCCESS_SEND_PASSWORD_EMAIL'),
    subText: '',
  };

  const sendEmailSucessPopup = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      title: '',
      content: (
        <AlertModalContent
          title={popupComment.title}
          mainText={popupComment.mainText}
          subText={popupComment.subText}
        />
      ),
      onClick() {
        history.push(pageUrl.auth.signIn);
      },
      onCancel() {
        history.push(pageUrl.auth.signIn);
      },
    });
  };

  useDidUpdateEffect(() => {
    if (!sendEmailForResetSuccess) return;
    history.push(pageUrl.auth.signIn);
    // sendEmailSucessPopup();
  }, [sendEmailForResetSuccess]);
  return (
    <AuthLayout
      title={<T>GLOBAL_RESET_PASSWORD</T>}
      buttonText={<T>GLOBAL_SEND</T>}
      linkContent={
        <div className="authLayout__link_row">
          <CustomSpan fontColor={color.gray_font}>
            <T>AUTH_REMEMBER_PASSWORD</T>
          </CustomSpan>
          <button onClick={handleNavigateLogin}>
            <CustomSpan fontWeight={500}>
              <T>GLOBAL_LOGIN</T>
            </CustomSpan>
          </button>
        </div>
      }
      isValidForm={isValidForm}
      onSubmit={handleSubmit}
    >
      <Grid item xs={12} className="authLayout__form_description">
        <Trans
          defaults="AUTH_ENTER_YOUR_EMAIL"
          components={[
            <CustomSpan fontColor={color.gray_week_font}></CustomSpan>,
            <CustomSpan fontColor={color.red} fontWeight="bold"></CustomSpan>,
          ]}
        />
        <p>
          <T>AUTH_SEND_PASSWORD_URL</T>
        </p>
      </Grid>
      <Grid item xs={12} className="authLayout__form_row">
        <MuiWrapper>
          <TextField
            id="nickname"
            name="nickname"
            placeholder="groupCode"
            inputProps={{
              'aria-label': 'nickname',
            }}
            variant="outlined"
            fullWidth
            autoComplete="off"
            error={isInvalidGroupCode}
            value={nickname.value}
            onChange={nickname.onChange}
          />
        </MuiWrapper>
        {isInvalidGroupCode && (
          <CustomText className="form__error_text">
            <T>REGISTER_NOT_VALID</T>
          </CustomText>
        )}
      </Grid>

      <Grid item xs={12} className="authLayout__form_row">
        <MuiWrapper>
          <TextField
            id="email"
            name="email"
            placeholder="email"
            variant="outlined"
            fullWidth
            autoComplete="off"
            error={isInvalidEmail || isInvalidEmployee}
            value={email.value}
            onChange={email.onChange}
            onKeyUp={e => e.key === 'Enter' && handleSubmit()}
          />
        </MuiWrapper>

        {isInvalidEmail && (
          <CustomText className="form__error_text">
            <T>REGISTER_NOT_VALID</T>
          </CustomText>
        )}
        {isInvalidEmployee && (
          <CustomText className="form__error_text">
            <T>ALERT_INVALID_EMPLOYEE</T>
          </CustomText>
        )}
      </Grid>
    </AuthLayout>
  );
}
